import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import {
  NotificationCursor,
  notificationsService,
} from '../services/notificationsService';
import type { Notification } from '../types';

export interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  fetchNotifications: () => void;
  loadMore: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [counterExists, setCounterExists] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const cursorRef = useRef<NotificationCursor | null>(null);

  useEffect(() => {
    if (!user?.id) {
      setNotifications([]);
      setUnreadCount(0);
      setCounterExists(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribeNotifications = notificationsService.subscribe(
      user.id,
      (items, cursor) => {
        setNotifications(items);
        cursorRef.current = cursor;
        setHasMore(items.length === 50);
        setLoading(false);
      },
      () => {
        setError('تعذر تحميل الإشعارات. حاول مرة أخرى.');
        setLoading(false);
      }
    );

    const unsubscribeCounter = notificationsService.subscribeUnreadCount(
      user.id,
      (count, exists) => {
        setUnreadCount(count);
        setCounterExists(exists);
      },
      () => setError('تعذر تحميل عداد الإشعارات.')
    );

    return () => {
      unsubscribeNotifications();
      unsubscribeCounter();
    };
  }, [user?.id, refreshKey]);

  const markAsRead = useCallback(async (notificationId: string) => {
    await notificationsService.markAsRead(notificationId);
  }, []);

  const markAllAsRead = useCallback(async () => {
    await notificationsService.markAllAsRead();
  }, []);

  const deleteNotification = useCallback(async (notificationId: string) => {
    await notificationsService.deleteNotification(notificationId);
  }, []);

  const fetchNotifications = useCallback(() => {
    setRefreshKey(key => key + 1);
  }, []);

  const loadMore = useCallback(async () => {
    if (!user?.id || !cursorRef.current || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const page = await notificationsService.loadMore(user.id, cursorRef.current);
      cursorRef.current = page.cursor;
      setHasMore(page.notifications.length === 50);
      setNotifications(current => {
        const merged = new Map(current.map(item => [item.id, item]));
        page.notifications.forEach(item => merged.set(item.id, item));
        return [...merged.values()].sort(
          (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
        );
      });
    } catch {
      setError('تعذر تحميل المزيد من الإشعارات.');
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, user?.id]);

  const effectiveUnreadCount = counterExists
    ? unreadCount
    : notifications.filter(notification => !notification.read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount: effectiveUnreadCount,
        loading,
        loadingMore,
        hasMore,
        error,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        fetchNotifications,
        loadMore,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};
